import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

export interface RecipeIngredient {
  id: string;
  ingredient_name: string;
  quantity: number;
  unit: string;
}

export interface SavedRecipe {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  servings: number;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  instructions: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
  equipment?: string[];
  created_at: string;
  updated_at: string;
  ingredients?: RecipeIngredient[];
}

export const useSavedRecipes = () => {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<SavedRecipe[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRecipes = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch recipes
      const { data: recipesData, error: recipesError } = await supabase
        .from('saved_recipes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (recipesError) throw recipesError;

      // Fetch ingredients for all recipes
      if (recipesData && recipesData.length > 0) {
        const recipeIds = recipesData.map(r => r.id);
        const { data: ingredientsData, error: ingredientsError } = await supabase
          .from('recipe_ingredients')
          .select('*')
          .in('recipe_id', recipeIds);

        if (ingredientsError) throw ingredientsError;

        // Map ingredients to recipes
        const recipesWithIngredients = recipesData.map(recipe => ({
          ...recipe,
          ingredients: ingredientsData?.filter(ing => ing.recipe_id === recipe.id) || []
        }));

        setRecipes(recipesWithIngredients);
      } else {
        setRecipes([]);
      }
    } catch (error) {
      console.error('Error fetching recipes:', error);
      toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  const createRecipe = async (
    recipeData: Omit<SavedRecipe, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'ingredients'>,
    ingredients: Omit<RecipeIngredient, 'id' | 'recipe_id'>[]
  ) => {
    if (!user) {
      toast.error('You must be logged in to save recipes');
      return null;
    }

    try {
      // Insert recipe
      const { data: recipe, error: recipeError } = await supabase
        .from('saved_recipes')
        .insert([{ ...recipeData, user_id: user.id }])
        .select()
        .single();

      if (recipeError) throw recipeError;

      // Insert ingredients
      if (ingredients.length > 0) {
        const ingredientsToInsert = ingredients.map(ing => ({
          ...ing,
          recipe_id: recipe.id
        }));

        const { error: ingredientsError } = await supabase
          .from('recipe_ingredients')
          .insert(ingredientsToInsert);

        if (ingredientsError) throw ingredientsError;
      }

      await fetchRecipes();
      toast.success('Recipe saved successfully!');
      return recipe;
    } catch (error) {
      console.error('Error creating recipe:', error);
      toast.error('Failed to save recipe');
      return null;
    }
  };

  const deleteRecipe = async (recipeId: string) => {
    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    try {
      const { error } = await supabase
        .from('saved_recipes')
        .delete()
        .eq('id', recipeId)
        .eq('user_id', user.id);

      if (error) throw error;

      await fetchRecipes();
      toast.success('Recipe deleted');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Failed to delete recipe');
    }
  };

  const exportShoppingList = (selectedRecipeIds: string[]) => {
    const selectedRecipes = recipes.filter(r => selectedRecipeIds.includes(r.id));
    
    // Aggregate ingredients
    const ingredientMap = new Map<string, { quantity: number; unit: string }>();
    
    selectedRecipes.forEach(recipe => {
      recipe.ingredients?.forEach(ing => {
        const key = `${ing.ingredient_name.toLowerCase()}|${ing.unit.toLowerCase()}`;
        const existing = ingredientMap.get(key);
        
        if (existing) {
          existing.quantity += ing.quantity;
        } else {
          ingredientMap.set(key, {
            quantity: ing.quantity,
            unit: ing.unit
          });
        }
      });
    });

    // Create PDF
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Add light grey background
    doc.setFillColor(240, 240, 240);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Title - using bold font
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(0, 0, 0);
    doc.text('Shopping List', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(new Date().toLocaleDateString(), pageWidth / 2, yPos, { align: 'center' });
    
    // Recipes
    yPos += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 30);
    doc.text('Recipes:', 20, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(70, 70, 70);
    selectedRecipes.forEach(recipe => {
      doc.text(`• ${recipe.title}`, 25, yPos);
      yPos += 7;
    });
    
    // Ingredients
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 30);
    doc.text('Ingredients:', 20, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(70, 70, 70);
    
    const sortedIngredients = Array.from(ingredientMap.entries()).sort();
    sortedIngredients.forEach(([key, value]) => {
      const [name] = key.split('|');
      const text = `☐ ${value.quantity} ${value.unit} ${name}`;
      
      if (yPos > 270) {
        doc.addPage();
        // Add light grey background to new page
        doc.setFillColor(240, 240, 240);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = 20;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(70, 70, 70);
      }
      
      doc.text(text, 25, yPos);
      yPos += 7;
    });

    // Save PDF
    doc.save(`shopping-list-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Shopping list downloaded!');
  };

  const importRecipeAsMeal = async (recipeId: string, mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack' = 'lunch') => {
    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    const recipe = recipes.find(r => r.id === recipeId);
    if (!recipe) {
      toast.error('Recipe not found');
      return;
    }

    try {
      // Create saved meal
      const { data: savedMeal, error: mealError } = await supabase
        .from('saved_meals')
        .insert([{
          user_id: user.id,
          name: recipe.title,
          description: recipe.description || `From recipe: ${recipe.title}`,
          meal_type: mealType,
          total_calories: recipe.calories,
          total_protein: recipe.protein,
          total_carbs: recipe.carbs,
          total_fat: recipe.fat,
          total_fiber: recipe.fiber || 0
        }])
        .select()
        .single();

      if (mealError) throw mealError;

      // Add a single item representing the entire recipe
      // We can't accurately split nutrition per ingredient without detailed data,
      // so we store the recipe as one complete meal item
      if (recipe.ingredients && recipe.ingredients.length > 0) {
        const ingredientsList = recipe.ingredients
          .map(ing => `${ing.quantity} ${ing.unit} ${ing.ingredient_name}`)
          .join(', ');
        
        const mealItem = {
          saved_meal_id: savedMeal.id,
          food_name: `${recipe.title} (${recipe.servings} serving${recipe.servings > 1 ? 's' : ''})`,
          quantity: recipe.servings,
          unit: 'serving',
          calories: recipe.calories,
          protein: recipe.protein,
          carbs: recipe.carbs,
          fat: recipe.fat,
          fiber: recipe.fiber || 0
        };

        const { error: itemsError } = await supabase
          .from('saved_meal_items')
          .insert([mealItem]);

        if (itemsError) throw itemsError;
      }

      toast.success('Recipe imported as saved meal!');
      return savedMeal;
    } catch (error) {
      console.error('Error importing recipe as meal:', error);
      toast.error('Failed to import recipe');
      return null;
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, [user]);

  return {
    recipes,
    loading,
    createRecipe,
    deleteRecipe,
    exportShoppingList,
    importRecipeAsMeal,
    refetch: fetchRecipes
  };
};
